import { useEffect } from "react";

const CANONICAL_ORIGIN = "https://quentinyoung.ca";

function setMetaByName(name, content) {
  if (content === undefined || content === null) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setMetaByProperty(property, content) {
  if (content === undefined || content === null) return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!data) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = id;
  s.text = JSON.stringify(data);
  document.head.appendChild(s);
}

/**
 * Meta - lightweight head manager for React SPAs.
 *
 * Props:
 *   title           - full <title>. If omitted, uses default site title.
 *   description     - meta description (max ~160 chars).
 *   path            - current path, used for canonical + og:url. Defaults to window.location.pathname.
 *   ogImage         - absolute or path OG image. Defaults to /og-image.jpg.
 *   type            - "website" | "article"
 *   breadcrumbs     - [{ name, url }] where url is relative or absolute
 *   article         - { headline, description, datePublished, author } for Article schema
 */
export const Meta = ({
  title,
  description,
  path,
  ogImage,
  type = "website",
  breadcrumbs,
  article,
  jsonLd,
  noindex = false,
}) => {
  useEffect(() => {
    const p = path || window.location.pathname || "/";
    const url = `${CANONICAL_ORIGIN}${p === "/" ? "/" : p.replace(/\/$/, "")}`;
    const fullTitle = title || "Quentin Young | Canadian Entrepreneur, Founder & Strategic Operator";
    const desc = description || "QuentinYoung.ca is the official site of Quentin Young: Canadian entrepreneur, founder, operator, brand strategist, and creative leader exploring human nature, culture, systems, leadership, craft, and the conditions that shape individual and collective potential.";
    const img = ogImage
      ? (ogImage.startsWith("http") ? ogImage : `${CANONICAL_ORIGIN}${ogImage}`)
      : `${CANONICAL_ORIGIN}/og-image.jpg`;

    document.title = fullTitle;
    setMetaByName("description", desc);
    setLink("canonical", url);

    // Open Graph
    setMetaByProperty("og:type", type);
    setMetaByProperty("og:site_name", "Quentin Young");
    setMetaByProperty("og:title", fullTitle);
    setMetaByProperty("og:description", desc);
    setMetaByProperty("og:url", url);
    setMetaByProperty("og:image", img);
    setMetaByProperty("og:image:width", "1200");
    setMetaByProperty("og:image:height", "630");
    setMetaByProperty("og:locale", "en_CA");

    // Twitter
    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", fullTitle);
    setMetaByName("twitter:description", desc);
    setMetaByName("twitter:image", img);

    // Robots
    if (noindex) {
      setMetaByName("robots", "noindex, nofollow");
    } else {
      setMetaByName("robots", "index, follow, max-image-preview:large");
    }

    // BreadcrumbList
    if (breadcrumbs && breadcrumbs.length > 0) {
      const list = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: b.url.startsWith("http") ? b.url : `${CANONICAL_ORIGIN}${b.url}`,
        })),
      };
      setJsonLd("ld-breadcrumbs", list);
    } else {
      const e = document.getElementById("ld-breadcrumbs");
      if (e) e.remove();
    }

    // Article schema for Thinking detail pages
    if (article) {
      const art = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.headline,
        description: article.description,
        mainEntityOfPage: url,
        author: { "@type": "Person", name: "Quentin Young", url: CANONICAL_ORIGIN },
        publisher: { "@type": "Person", name: "Quentin Young", url: CANONICAL_ORIGIN },
        inLanguage: "en",
      };
      if (article.datePublished) art.datePublished = article.datePublished;
      setJsonLd("ld-article", art);
    } else {
      const e = document.getElementById("ld-article");
      if (e) e.remove();
    }

    // Arbitrary per-page JSON-LD (e.g. Service schema). Tracked so it can be cleaned up.
    const injectedIds = (jsonLd || []).map((s) => s.id);
    (jsonLd || []).forEach((s) => setJsonLd(s.id, s.data));
    return () => {
      injectedIds.forEach((id) => {
        const e = document.getElementById(id);
        if (e) e.remove();
      });
    };
  }, [title, description, path, ogImage, type, breadcrumbs, article, jsonLd]);

  return null;
};

export default Meta;
